import { find, get, map, mean, reduce, sum } from "lodash";
import { Facet } from "@linkfluence/models/facet";
import { Metric } from "@linkfluence/models/metric";
import { SearchFragmentDateTerm } from "@radarly/models/search";

type Dots = SearchFragmentDateTerm["data"]["aggs"]["date"]["values"];
type Dot = {
  key: string;
  counts: Partial<Record<Metric, number>>;
  raw_doc_count?: number;
  aggs: {
    [term: string]: {
      values: { key: string; counts: Partial<Record<Metric, number>> }[];
    };
  };
};

const initializeBucketTerms = (dots: Dots, facet: string) => {
  return dots.reduce((acc, dot) => {
    dot.aggs[facet]?.values.forEach(({ key }) => {
      if (!acc[key]) {
        facet === Facet.Focuses
          ? (acc[key.split("_")[1]] = [])
          : (acc[key] = []);
      }
    });
    return acc;
  }, {});
};

function findDotCountFromFacetTerm(
  dot: Dot,
  facet: string,
  term: string,
  metric: Metric
) {
  const result = find(dot.aggs[facet].values, {
    key: facet === Facet.Focuses ? "focus_" + term : term,
  });
  return result ? result.counts[metric] || 0 : 0;
}

function formatDotFields(
  fields: string[],
  dot: Dot,
  facet: string,
  metric: Metric
) {
  return fields.map((field) => ({
    count: findDotCountFromFacetTerm(dot, facet, field, metric),
    date: dot.key,
    term: field,
  }));
}

export function computeBucketDataByTerm(
  data: SearchFragmentDateTerm["data"],
  facet: string,
  metric: Metric
): Record<string, { count: number; date: string }[]> {
  if (!data || !facet) return {};
  const terms = initializeBucketTerms(data.aggs.date.values, facet);
  const fields = Object.keys(terms);
  return data.aggs.date.values.reduce((acc, dot) => {
    const dotFields = formatDotFields(fields, dot, facet, metric);
    dotFields.forEach(({ count, date, term }) =>
      acc[term].push({ count, date })
    );
    return acc;
  }, terms);
}

export function computeBucketDataByDate(
  data: SearchFragmentDateTerm["data"],
  facet: string,
  metric: Metric
) {
  return map(data ? data.aggs.date.values : [], (dot) => {
    return {
      date: dot.key,
      data: reduce(
        dot.aggs[facet] ? dot.aggs[facet].values : [],
        (acc: Record<string, number>, entry) => {
          const key =
            facet === Facet.Focuses ? parseInt(entry.key.substr(6)) : entry.key;
          acc[key] = entry.counts[metric] || 0;
          return acc;
        },
        {}
      ),
    };
  });
}

export function computeData(
  data: SearchFragmentDateTerm["data"],
  facet: string,
  metric: Metric,
  average?: boolean
): Record<string, number> {
  const dataByTerm = reduce(
    data ? data.aggs.date.values : [],
    (acc, dot) => {
      return reduce(
        get(dot, ["aggs", facet, "values"]),
        (acc, entry) => {
          const key =
            facet === Facet.Focuses ? parseInt(entry.key.substr(6)) : entry.key;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(entry.counts[metric] || 0);
          return acc;
        },
        acc
      );
    },
    {}
  );
  return reduce(
    dataByTerm,
    (acc, counts, key) => {
      acc[key] = average ? mean(counts) : sum(counts);
      return acc;
    },
    {}
  );
}
